import { SPREADSHEET_STEPS } from '@/components/Company/importer/constants';
import {
  API_METHOD_KEY,
  isPortalFileMethod,
} from '@/components/Company/importer/wizard/originOptions';

// Each flow numbers its steps differently, so `activeStep` alone is ambiguous
// (step 3 is the portal file upload and also the contacts column mapping).
// `resolveWizardStep` is the single place that crosses the step number with the
// flow; everything downstream keys off the resulting step id.
export const WIZARD_STEP = {
  CHOOSE_ORIGIN: 'choose_origin',
  CHOOSE_CRM_ENTITIES: 'choose_crm_entities',
  UPLOAD_FILES: 'upload_files',
  API_CREDENTIALS: 'api_credentials',
  API_PREVIEW: 'api_preview',
  REVIEW_MAPPING: 'review_mapping',
  MAP_LEAD_STATUS: 'map_lead_status',
  MAP_AGENTS: 'map_agents',
  IMPORT_SETTINGS: 'import_settings',
  CONFIRM_IMPORT: 'confirm_import',
  PROGRESS: 'progress',
  UNKNOWN: 'unknown',
};

const ORIGIN_STEP = 1;

const PORTAL_STEPS = {
  ORIGIN: 1,
  FILE: 2,
  CONFIRM: 3,
  PROGRESS: 4,
};

// A CRM bundle covers several entities, so the flow adds an entity selection
// between the origin and the upload and every later step shifts by one.
export const CRM_STEPS = {
  ORIGIN: 1,
  ENTITIES: 2,
  UPLOAD: 3,
  CONFIRM: 4,
  PROGRESS: 5,
};

export const CRM_MODE = 'crm';

const CRM_STEP_IDS = {
  [CRM_STEPS.ENTITIES]: WIZARD_STEP.CHOOSE_CRM_ENTITIES,
  [CRM_STEPS.UPLOAD]: WIZARD_STEP.UPLOAD_FILES,
  [CRM_STEPS.CONFIRM]: WIZARD_STEP.CONFIRM_IMPORT,
  [CRM_STEPS.PROGRESS]: WIZARD_STEP.PROGRESS,
};

const resolveCrmStep = (activeStep, methodKey) => {
  if (activeStep === CRM_STEPS.UPLOAD && methodKey === API_METHOD_KEY) {
    return WIZARD_STEP.API_CREDENTIALS;
  }
  return CRM_STEP_IDS[activeStep] ?? WIZARD_STEP.UNKNOWN;
};

const SPREADSHEET_MODES = ['contacts', 'owners'];

export const isSpreadsheetMode = (mode) => SPREADSHEET_MODES.includes(mode);

export const isCrmMode = (mode) => mode === CRM_MODE;

export const resolveDeepLinkEntity = (query = {}) =>
  isSpreadsheetMode(query.entity) ? query.entity : null;

const SPREADSHEET_STEP_IDS = {
  [SPREADSHEET_STEPS.UPLOAD]: WIZARD_STEP.UPLOAD_FILES,
  [SPREADSHEET_STEPS.MAPPING]: WIZARD_STEP.REVIEW_MAPPING,
  [SPREADSHEET_STEPS.LEAD_STATUS]: WIZARD_STEP.MAP_LEAD_STATUS,
  [SPREADSHEET_STEPS.AGENTS]: WIZARD_STEP.MAP_AGENTS,
  [SPREADSHEET_STEPS.SETTINGS]: WIZARD_STEP.IMPORT_SETTINGS,
  [SPREADSHEET_STEPS.PROGRESS]: WIZARD_STEP.PROGRESS,
};

const isPortalOrigin = (methodKey) =>
  isPortalFileMethod(methodKey) || methodKey === API_METHOD_KEY;

const resolvePortalStep = (activeStep, methodKey) => {
  switch (activeStep) {
    case PORTAL_STEPS.ORIGIN:
      return WIZARD_STEP.CHOOSE_ORIGIN;
    case PORTAL_STEPS.FILE:
      if (isPortalFileMethod(methodKey)) return WIZARD_STEP.UPLOAD_FILES;
      return methodKey === API_METHOD_KEY
        ? WIZARD_STEP.API_PREVIEW
        : WIZARD_STEP.UNKNOWN;
    case PORTAL_STEPS.CONFIRM:
      return WIZARD_STEP.CONFIRM_IMPORT;
    case PORTAL_STEPS.PROGRESS:
      return isPortalOrigin(methodKey)
        ? WIZARD_STEP.PROGRESS
        : WIZARD_STEP.UNKNOWN;
    default:
      return WIZARD_STEP.UNKNOWN;
  }
};

export const resolveWizardStep = ({ activeStep, mode, methodKey } = {}) => {
  if (activeStep === ORIGIN_STEP) return WIZARD_STEP.CHOOSE_ORIGIN;
  if (isSpreadsheetMode(mode)) {
    return SPREADSHEET_STEP_IDS[activeStep] ?? WIZARD_STEP.UNKNOWN;
  }
  if (isCrmMode(mode)) {
    return resolveCrmStep(activeStep, methodKey);
  }
  if (mode === 'portal_crm') return resolvePortalStep(activeStep, methodKey);
  return WIZARD_STEP.UNKNOWN;
};

const PORTAL_CAPABILITIES = {
  uploadsOnDrop: false,
  configuresJobOnNext: false,
  tracksRequiredFiles: false,
  readyFromUploadedFiles: false,
  usesEntityUploadCopy: false,
  supportsImportOwners: true,
  uploadingStatus: (statuses) => statuses.uploadStatus,
};

const SPREADSHEET_CAPABILITIES = {
  ...PORTAL_CAPABILITIES,
  readyFromUploadedFiles: true,
  usesEntityUploadCopy: true,
};

const CRM_CAPABILITIES = {
  uploadsOnDrop: true,
  configuresJobOnNext: true,
  tracksRequiredFiles: true,
  skipsRequiredFilesFetch: false,
  readyFromUploadedFiles: true,
  usesEntityUploadCopy: false,
  supportsImportOwners: false,
  // Bundle parsing runs async after the POST resolves: keep the loader up
  // until useCrmConfigPolling reports a terminal crmConfigParseStatus too,
  // not just until the (fast) enqueue request itself resolves.
  uploadingStatus: (statuses) => {
    if (
      statuses.crmConfigStatus === 'loading' ||
      statuses.crmConfigParseStatus === 'running'
    ) {
      return 'loading';
    }
    return statuses.crmConfigStatus;
  },
};

const CRM_API_CAPABILITIES = {
  uploadsOnDrop: false,
  configuresJobOnNext: true,
  tracksRequiredFiles: false,
  skipsRequiredFilesFetch: true,
  readyFromUploadedFiles: false,
  usesEntityUploadCopy: false,
  supportsImportOwners: false,
  uploadingStatus: (statuses) => {
    if (
      statuses.uploadStatus === 'loading' ||
      statuses.crmConfigStatus === 'loading'
    ) {
      return 'loading';
    }
    return statuses.uploadStatus;
  },
};

export const resolveFlowCapabilities = (mode, originOption) => {
  if (isCrmMode(mode)) {
    if (originOption?.methodKey === API_METHOD_KEY) {
      return CRM_API_CAPABILITIES;
    }
    return CRM_CAPABILITIES;
  }
  if (isSpreadsheetMode(mode)) return SPREADSHEET_CAPABILITIES;
  return PORTAL_CAPABILITIES;
};

export const isValueMappingStep = (step) =>
  step === WIZARD_STEP.MAP_LEAD_STATUS || step === WIZARD_STEP.MAP_AGENTS;

// The progress screen has nothing to go back to and owns its own navigation.
export const hidesFooter = (step) => step === WIZARD_STEP.PROGRESS;

const STEPS_WITH_NEXT = [
  WIZARD_STEP.CHOOSE_CRM_ENTITIES,
  WIZARD_STEP.API_CREDENTIALS,
  WIZARD_STEP.UPLOAD_FILES,
  WIZARD_STEP.REVIEW_MAPPING,
  WIZARD_STEP.MAP_LEAD_STATUS,
  WIZARD_STEP.MAP_AGENTS,
  WIZARD_STEP.IMPORT_SETTINGS,
  WIZARD_STEP.CONFIRM_IMPORT,
];

export const hasNextAction = (step) => STEPS_WITH_NEXT.includes(step);

// What blocks the Next button, per step. Steps missing from the map never
// block it.
const NEXT_BLOCKED_BY_STEP = {
  [WIZARD_STEP.CHOOSE_CRM_ENTITIES]: (flow) => !flow.hasCrmInstances,
  [WIZARD_STEP.API_CREDENTIALS]: (flow) =>
    flow.apiCredentialsStatus !== 'succeeded' ||
    flow.uploadStatus === 'loading' ||
    flow.crmConfigStatus === 'loading',
  [WIZARD_STEP.UPLOAD_FILES]: (flow) =>
    (!flow.hasPendingFiles && !flow.hasUploadReady) ||
    flow.uploadStatus === 'loading' ||
    flow.crmConfigStatus === 'loading' ||
    // A CRM bundle is only complete once every required workbook has been
    // uploaded and accepted; a missing or rejected one blocks Next.
    flow.hasIncompleteRequiredFiles === true,
  [WIZARD_STEP.REVIEW_MAPPING]: (flow) =>
    flow.mappingStatus === 'loading' ||
    flow.mappingSaveStatus === 'loading' ||
    // Name, contact method, and owners' property reference are hard blocks.
    // Optional columns still in review no longer disable Next — clicking it
    // opens a confirmation modal instead.
    flow.isMissingRequiredFields ||
    flow.isMissingPropertyReferenceCode ||
    // Footer banners handle remaining mapping blocks (e.g. duplicate
    // highlander): replace actions and return on Next.
    (!flow.isMappingValid && !flow.hasMappingFooterBlock),
  [WIZARD_STEP.MAP_LEAD_STATUS]: (flow) =>
    flow.valueMappingsStatus === 'loading' || !flow.isLeadStatusComplete,
  [WIZARD_STEP.MAP_AGENTS]: (flow) =>
    flow.valueMappingsStatus === 'loading' || !flow.isAgentsComplete,
  [WIZARD_STEP.IMPORT_SETTINGS]: (flow) =>
    flow.startStatus === 'succeeded' || !flow.isImportSettingsComplete,
  [WIZARD_STEP.CONFIRM_IMPORT]: (flow) =>
    flow.startStatus === 'succeeded' ||
    flow.startStatus === 'loading' ||
    !flow.hasRecordsToImport,
};

export const isNextDisabled = (step, flow = {}) =>
  Boolean(NEXT_BLOCKED_BY_STEP[step]?.(flow));
